import { useEffect } from 'react';
import type { AgentTerminalDisplay } from '@/components/desktop-panel-primitives';
import type { BackgroundTerminalCompletion } from '@/lib/conversations/message-part/terminal';
import { completedBackgroundTerminals } from '@/lib/desktop-shell/message-derivations';
import { readNativeAgentBackgroundOutput } from '@/lib/native-desktop';

const RECONCILE_INTERVAL_MS = 1_500;

/**
 * Reconciles durable background-task output into the conversation's terminal
 * state. This runs independently of the side panel so transcript cards,
 * Summary, and the terminal pane all consume the same stopped signal.
 */
export function useBackgroundTerminalReconciliation(
  terminals: readonly AgentTerminalDisplay[],
  onStopped: (terminalId: string, completion?: BackgroundTerminalCompletion) => void,
): void {
  useEffect(() => {
    const candidates = terminals.filter(
      (terminal) =>
        terminal.outputFile !== undefined &&
        terminal.signal === null &&
        terminal.backgroundCompleted !== true,
    );
    if (candidates.length === 0) return;
    let cancelled = false;
    const reported = new Set<string>();
    const reconcile = () => {
      void completedBackgroundTerminals(candidates, readNativeAgentBackgroundOutput).then(
        (items) => {
          if (cancelled) return;
          for (const { id, completion } of items) {
            if (reported.has(id)) continue;
            reported.add(id);
            onStopped(id, completion);
          }
        },
      );
    };
    reconcile();
    const interval = window.setInterval(reconcile, RECONCILE_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [onStopped, terminals]);
}
